// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/pages/Home.tsx
// v2.0.0 - Dynamische Komponenten-Reihenfolge aus CMS

import React, { useEffect, useState } from 'react';
import { useLanguage } from '../lang/index.lang.tsx';

// Komponenten importieren
import HeroSlider from '../components/HeroSlider.tsx';
import ContentCard from '../components/ContentCard.tsx';
import PartnerGrid from '../components/PartnerGrid.tsx';
import CareersBanner from '../components/CareersBanner.tsx';
import Showcase from '../components/Showcase.tsx';
import ContactPerson from '../components/ContactPerson.tsx';
import FocusCarousel from '../components/FocusCarousel.tsx';

// ============================================
// TYPES
// ============================================
interface PageComponent {
  component_name: string;
  sort_order: number;
  props: Record<string, any>;
}

interface PageData {
  slug: string;
  name: string;
  path: string;
  components: PageComponent[];
}

// ============================================
// HOME PAGE
// ============================================
const Home = () => {
  const { t } = useLanguage();
  const [componentOrder, setComponentOrder] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // Komponenten-Reihenfolge aus CMS laden
  useEffect(() => {
    const fetchPageOrder = async () => {
      try {
        const response = await fetch('/system/api/v1/website/public/page?path=/');
        if (response.ok) {
          const data: PageData = await response.json();
          // Sortieren und Namen extrahieren
          const order = data.components
            .sort((a, b) => a.sort_order - b.sort_order)
            .map(c => c.component_name);
          setComponentOrder(order);
        }
      } catch (err) {
        console.error('Error loading page order:', err);
        // Fallback: Standard-Reihenfolge
        setComponentOrder([
          'HeroSlider',
          'ContentCard',
          'PartnerGrid',
          'CareersBanner',
          'Showcase',
          'ContactPerson',
          'FocusCarousel'
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchPageOrder();
  }, []);

  // ============================================
  // KOMPONENTEN MIT PROPS
  // ============================================
  const components: Record<string, React.ReactNode> = {
    HeroSlider: (
      <HeroSlider 
        key="HeroSlider"
        title={t("home.sustainableThinking.title")}
        description={t("home.welcome.description")}
        buttonText={t("common.learnMore")}
        buttonLink="/about"
      />
    ),

    ContentCard: (
      <ContentCard 
        key="ContentCard"
        heading={{
          title: t("home.whatWeDo.title"),
          description: t("home.sustainableThinking.description"),
        }}
        data={[
          {
            id: 1,
            title: t("home.insightGrid.insights.0.title"),
            description: t("home.insightGrid.insights.0.description"),
            image: "/images/EST-468599638.jpg",
            category: t("home.insightGrid.insights.0.category"),
            buttonText: t("home.insightGrid.insights.0.buttonText"),
            buttonLink: "/about",
          },
          {
            id: 2,
            title: t("home.insightGrid.insights.1.title"),
            description: t("home.insightGrid.insights.1.description"),
            image: "/images/EST-767664.jpg",
            category: t("home.insightGrid.insights.1.category"),
            buttonText: t("home.insightGrid.insights.1.buttonText"),
            buttonLink: "/services",
          },
          {
            id: 3,
            title: t("home.insightGrid.insights.2.title"),
            description: t("home.insightGrid.insights.2.description"),
            image: "/images/EST-1188930630.jpg",
            category: t("home.insightGrid.insights.2.category"),
            buttonText: t("home.insightGrid.insights.2.buttonText"),
            buttonLink: "/about",
          },
          {
            id: 4,
            title: t("home.insightGrid.insights.3.title"),
            description: t("home.insightGrid.insights.3.description"),
            image: "/images/EST-1284284200.jpg",
            category: t("home.insightGrid.insights.3.category"),
            buttonText: t("home.insightGrid.insights.3.buttonText"),
            buttonLink: "/careers",
          },
        ]}
      />
    ),

    PartnerGrid: <PartnerGrid key="PartnerGrid" />,

    CareersBanner: (
      <CareersBanner
        key="CareersBanner"
        subtitle={t("careers.subtitle")}
        title={t("careers.description_short")}
        description={t("careers.description_long")}
        buttonText={t("home.heroBanner.buttonText")}
        buttonLink="/careers"
      />
    ),

    Showcase: (
      <Showcase
        key="Showcase"
        heading={{
          subtitle: t("services.heading.subtitle"),
          title: t("services.heading.title"),
          explore: t("services.heading.title"),
          viewAllText: t("services.heading.viewAll"),
          viewAllLink: "/services",
        }}
        items={[
          {
            id: 1,
            title: t("services.design-build.title"),
            subtitle: t("services.design-build.description.short"),
            description: t("services.design-build.description.long"),
            link: "/services/design-build",
          },
          {
            id: 2,
            title: t("services.general-planning.title"),
            subtitle: t("services.general-planning.description.short"),
            description: t("services.general-planning.description.long"),
            link: "/services/general-planning",
          },
          {
            id: 3,
            title: t("services.construction-services.title"),
            subtitle: t("services.construction-services.description.short"),
            description: t("services.construction-services.description.long"),
            link: "/services/construction-services",
          },
          {
            id: 4,
            title: t("services.due-diligence.title"),
            subtitle: t("services.due-diligence.description.short"),
            description: t("services.due-diligence.description.long"),
            link: "/services/due-diligence",
          },
          {
            id: 5,
            title: t("services.energy-advisory.title"),
            subtitle: t("services.energy-advisory.description.short"),
            description: t("services.energy-advisory.description.long"),
            link: "/services/energy-advisory",
          },
          {
            id: 6,
            title: t("services.development-strategies.title"),
            subtitle: t("services.development-strategies.description.short"),
            description: t("services.development-strategies.description.long"),
            link: "/services/development-strategies",
          },
          {
            id: 7,
            title: t("services.fire-protection.title"),
            subtitle: t("services.fire-protection.description.short"),
            description: t("services.fire-protection.description.long"),
            link: "/services/fire-protection",
          },
        ]}
        learnMoreText={t("services.heading.learnMore")}
      />
    ),

    ContactPerson: (
      <ContactPerson
        key="ContactPerson"
        heading={{
          subtitle: t("home.welcome.title"),
          title: t("services.heading.contact-person"),
        }}
        employeeIds={["SR", "AS"]}
      />
    ),

    // ESG nutzt auch ContentCard
    ESG: (
      <ContentCard
        key="ESG"
        heading={{
          title: t("home.esg.heading.title"),
          description: t("home.esg.heading.description"),
        }}
        data={[
          {
            id: 1,
            title: t("home.esg.s.title"),
            description: t("home.esg.s.description"),
            image: "/images/EST-1385648186.jpg",
            category: t("home.esg.s.category"),
            buttonText: t("home.esg.s.buttonText"),
            buttonLink: "/about",
          },
          {
            id: 2,
            title: t("home.esg.e.title"),
            description: t("home.esg.e.description"),
            image: "/images/EST-599972772.jpg",
            category: t("home.esg.e.category"),
            buttonText: t("home.esg.e.buttonText"),
            buttonLink: "/about",
          },
          {
            id: 3,
            title: t("home.esg.g.title"),
            description: t("home.esg.g.description"),
            image: "/images/EST-12499784456.jpg",
            category: t("home.esg.g.category"),
            buttonText: t("home.esg.g.buttonText"),
            buttonLink: "/about",
          },
        ]}
      />
    ),

    FocusCarousel: <FocusCarousel key="FocusCarousel" />,
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-gray-500">Loading...</span>
        </div>
      </div>
    );
  }

  // Komponenten in CMS-Reihenfolge rendern
  return (
    <>
      {componentOrder.map((name) => components[name] || null)}
    </>
  );
};

export default Home;
